import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExtAContext from '../../contexts/extAContext';
import { useParams } from 'react-router-dom';
import { configureUrlWithParams, formatDateWithTimezoneOffset } from '../../../../helpers/utils';
import type { RootState } from '../../../../state/store';
import { useSelector } from 'react-redux';
import type {
  GridPreDestroyedEvent,
  GridState,
  IServerSideGetRowsParams,
  StateUpdatedEvent
} from 'ag-grid-enterprise';
import { fetchData } from '../../../../helpers/offthreadFetcher';
import ReportDialog from '../reportDialog';
import SavedViewsDialog from '../savedViewsDialog';
import useExtAViewConfigs from '../../hooks/useExtAViewConfigs';
import SaveCurrentViewDialog from '../saveCurrViewDialog';
import CalculatedFieldDg from '../calculatedFieldDg';
import { reportExceptionToSentry } from '../../../../services/sentry/utils';
import type { ExtAContextTypes } from '../../types/extendedA';

interface ExtAProviderProps {
  children: ReactNode;
}

type ReportConfig = {
  title: string;
  widgetUrl: string;
  configs: null | PivotSchemaConfig;
};

type PivotSchemaConfig = {
  config: { fields: any[] };
  data: any[];
  title: string;
};

const ExtAProvider = ({ children }: ExtAProviderProps) => {
  const [reportConfigs, setReportConfigs] = useState<ReportConfig>({
    title: '',
    widgetUrl: '',
    configs: null
  });
  const routeMatch = useParams();
  /** When opening Extended A from Ask Vantage (/ask), URL has no :dashboard/:widget — pass explicit segments. */
  const [routeOverride, setRouteOverride] = useState<{ dashboard: string; widget: string } | null>(
    null
  );
  /** Ask Vantage / deep-link: date, time, location applied to SSRM instead of global Redux filters */
  const [filterOverrides, setFilterOverrides] = useState<{
    dates: [string, string];
    timeRange: [[number, number], [number, number]];
    locations?: number[];
  } | null>(null);
  const effectiveDashboard = routeOverride?.dashboard ?? routeMatch.dashboard;
  const effectiveWidget = routeOverride?.widget ?? routeMatch.widget;

  const [loadState, setLoadState] = useState({
    isLoading: false,
    isError: false
  });
  const [showReport, setShowReport] = useState(false);
  const [showSavedViewsDg, setShowSavedViewsDg] = useState(false);
  const [showSaveCurrentViewDg, setShowSaveCurrentViewDg] = useState(false);
  const [showCalculatedFieldDg, setShowCalculatedFieldDg] = useState(false);

  const [initialState, setInitialState] = useState<GridState>();
  const [currentState, setCurrentState] = useState<GridState>();
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Track if we've already validated the offset for dateTime columns
  const hasValidatedOffset = useRef<boolean>(false);

  const pivotTableInstance = useRef<any>(null);
  const pivotTableRef = (refProps: any) => {
    pivotTableInstance.current = refProps;
  };
  const getPivotTableInstance = () => {
    return pivotTableInstance.current;
  };

  const views = useExtAViewConfigs({
    dashboard: effectiveDashboard,
    dashboardWidget: effectiveWidget,
    widgetUrl: reportConfigs.widgetUrl,
    onViewSaveSuccess: () => {
      views.fetchViews();
      setShowSaveCurrentViewDg(false);
    },
    onViewUpdateSuccess: () => {
      views.fetchViews();
      setShowSavedViewsDg(false);
    },
    onViewDeleteSuccess: () => {
      views.fetchViews();
    }
  });

  const { company, filters } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details?.time_zone_offset;

  const effectiveFilters = useMemo(() => {
    if (!filterOverrides) return filters;
    return {
      ...filters,
      dates: filterOverrides.dates,
      timeRange: filterOverrides.timeRange,
      locations:
        filterOverrides.locations !== undefined ? filterOverrides.locations : filters.locations
    };
  }, [filters, filterOverrides]);

  const [colDefs, setColDefs] = useState([]);

  const formatValue = (defs: any[]) => {
    let updatedColDefs = [];
    updatedColDefs = defs.map((d) => {
      if (d.type == 'dateTime') {
        d.valueFormatter = (d: any) => {
          if (!d.value) {
            return '-';
          }
          return formatDateWithTimezoneOffset(d.value, timezoneOffset);
        };
      }
      // Add comma formatting for number columns
      else if (d.type == 'number' || d.type == 'currency') {
        d.valueFormatter = (params: any) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          // Format numbers with commas for thousands separators
          return params.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        };
      }
      return d;
    });
    return updatedColDefs;
  };

  const hideOffsetColumn = (defs: any) => {
    const offsetColIndex = defs.findIndex((d: any) => d.field === 'offset');
    if (offsetColIndex !== -1) {
      return defs.filter((d: any) => d.field !== 'offset');
    }
    return defs;
  };

  const configureFilters = (defs: any) => {
    const updatedColDefs = defs.map((d: any) => {
      if (d.filter) {
        if (!showFilterPanel) {
          setShowFilterPanel(true);
        }
      }
      return d;
    });
    return updatedColDefs;
  };

  useEffect(() => {
    if (reportConfigs.widgetUrl) {
      setShowReport(true);
      fetchReportConfigurations();
    } else {
      setShowReport(false);
      setShowFilterPanel(false);
    }
  }, [reportConfigs.widgetUrl]);

  useEffect(() => {
    if (reportConfigs.configs && reportConfigs.configs?.config) {
      setColDefs(
        configureFilters(hideOffsetColumn(formatValue(reportConfigs.configs.config.fields)))
      );
    }
  }, [reportConfigs.configs]);

  const ssrmUrl = useMemo(() => {
    const dashboard = effectiveDashboard;
    const widget = effectiveWidget;
    let baseUrl = `/api/v1/dar/${dashboard}/${widget}/${reportConfigs.widgetUrl}/extended`;
    let schemaUrl = `${baseUrl}/ssrm-schema`;
    if (dashboard && widget && reportConfigs.widgetUrl && effectiveFilters) {
      baseUrl = configureUrlWithParams(baseUrl, {
        ...effectiveFilters,
        additionalFilters: [],
        shouldUseEpocForTimeRange: true,
        timezoneOffset
      });
      schemaUrl = configureUrlWithParams(schemaUrl, {
        ...effectiveFilters,
        additionalFilters: [],
        shouldUseEpocForTimeRange: true,
        timezoneOffset
      });
    }
    return {
      /** URL for fetching SSRM row data */
      data: baseUrl,
      /** URL for getting initial data grid configurations */
      schema: schemaUrl
    };
  }, [effectiveDashboard, effectiveWidget, reportConfigs.widgetUrl, effectiveFilters, timezoneOffset]);

  /** SSRM instance for fetching row data */
  const serverSideDataSource = useMemo(
    () => ({
      getRows: (params: IServerSideGetRowsParams) => {
        const { success, fail } = params;
        // Fetch paginated row data
        fetchData(ssrmUrl.data, {
          method: 'POST',
          data: { body: params.request }
        })
          .then((res: any) => {
            const rowData = res.extended_data.data ?? [];

            // Check for missing offset when there's a dateTime column (only once)
            if (
              !hasValidatedOffset.current &&
              rowData.length > 0 &&
              colDefs?.length &&
              colDefs.find((col: any) => col.type === 'dateTime') &&
              !rowData[0].offset
            ) {
              hasValidatedOffset.current = true;
              console.error('Offset is not found in the data for column with type dateTime');
              reportExceptionToSentry({
                title: 'Extended-A SSRM Report Error',
                message:
                  'Offset is not found in the data for column with type dateTime. ' +
                  'Now using company timezone offset instead.',
                stack: new Error().stack as any,
                tags: {
                  reportTitle: reportConfigs.title || 'Unknown Report',
                  url: window.location.href
                }
              });
            }

            success({
              rowData,
              rowCount: rowData.length > 0 ? rowData[0].total_records : 0
            });
          })
          .catch((err: any) => {
            fail();
          });
      },
      destroy: () => {}
    }),
    [ssrmUrl.data, colDefs, reportConfigs.title]
  );

  /** function to fetch initial SSRM Configuration to load the report */
  const fetchReportConfigurations = useCallback(async () => {
    if (ssrmUrl.schema) {
      setLoadState({ isLoading: true, isError: false });
      try {
        const data: any = await fetchData(ssrmUrl.schema, { method: 'GET' });
        setReportConfigs((d) => ({
          ...d,
          title: data?.extended_data?.config?.report_title ?? '',
          configs: data?.extended_data
        }));
        setLoadState({ isLoading: false, isError: false });
      } catch (e) {
        console.log(e);
        setLoadState({ isLoading: false, isError: true });
      }
    }
  }, [ssrmUrl.schema]);

  const dialogHandler: ExtAContextTypes['dialogHandler'] = {
    showReport: ({
      widgetUrl = '',
      dashboard,
      dashboardWidget,
      dateRange,
      timeRange,
      locationIds
    } = {}) => {
      setReportConfigs((d) => ({ ...d, widgetUrl: widgetUrl }));
      if (dashboard && dashboardWidget) {
        setRouteOverride({ dashboard, widget: dashboardWidget });
      } else {
        setRouteOverride(null);
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        const tr: [[number, number], [number, number]] =
          timeRange ??
          ([
            [0, 0],
            [23, 59]
          ] as [[number, number], [number, number]]);
        setFilterOverrides({
          dates: [dateRange[0], dateRange[1]],
          timeRange: tr,
          locations: locationIds
        });
      } else {
        setFilterOverrides(null);
      }
    },
    hideReport: () => {
      // Clear all report specific state so that a fresh open doesn't carry
      // over column definitions, filter panel visibility or grid snapshots.
      setReportConfigs({ widgetUrl: '', configs: null, title: '' });
      setColDefs([]);
      setShowFilterPanel(false);
      setInitialState(undefined);
      setCurrentState(undefined);
      setRouteOverride(null);
      setFilterOverrides(null);
      // Reset offset validation flag
      hasValidatedOffset.current = false;
    }
  };

  const calcFieldDialogHandler = {
    showDialog: () => {
      setShowCalculatedFieldDg(true);
    },
    hideDialog: () => {
      setShowCalculatedFieldDg(false);
    }
  };

  const savedVwsDialogHandler = {
    showDialog: () => {
      views.fetchViews();
      setShowSavedViewsDg(true);
    },
    hideDialog: () => {
      setShowSavedViewsDg(false);
    }
  };

  const saveCurrentViewDialogHandler = {
    showDialog: () => {
      setShowSaveCurrentViewDg(true);
    },
    hideDialog: () => {
      setShowSaveCurrentViewDg(false);
    }
  };

  const viewControls = {
    saveCurrentView: ({ name = '' }) => {
      const snapshot = getPivotTableInstance().api.getColumnState();
      // let snapshot = currentState; // TODO: Full save state
      views.saveNewView({ name, snapshot });
    },
    overwriteView: (id: any) => {
      const viewDetails = views.savedViews.find((view: any) => view.id === id) ?? { name: '' };
      views.updateView({
        id,
        name: viewDetails?.name,
        snapshot: getPivotTableInstance().api.getColumnState()
        // snapshot: currentState, // TODO: Full save state
      });
    },
    deleteView: (id: any) => {
      views.deleteView({ id });
    },
    loadSelectedView: (snapshot: any) => {
      // setInitialState(snapshot); // TODO: Full save state
      getPivotTableInstance()?.api?.applyColumnState({ state: snapshot });
    }
  };

  const onGridPreDestroyed = useCallback((params: GridPreDestroyedEvent) => {
    const { state } = params;
    console.log('Grid state on destroy (can be persisted)', state);
    setInitialState(state);
  }, []);

  const onStateUpdated = useCallback((params: StateUpdatedEvent) => {
    console.log('State updated', params.state);
    setCurrentState(params.state);
  }, []);

  const printState = useCallback(() => {
    console.log('Grid state', currentState);
  }, [currentState]);

  // const gridState = {
  //   initialState,
  //   onGridPreDestroyed,
  //   onStateUpdated,
  //   printState,
  // };

  return (
    <ExtAContext.Provider
      value={{
        fetchReportConfigurations,
        serverSideDataSource,
        dialogHandler,
        loadState,
        reportConfigs,
        showReport,
        savedVwsDialogHandler,
        views,
        viewControls,
        colDefs,
        pivotTableRef,
        getPivotTableInstance,
        calcFieldDialogHandler,
        saveCurrentViewDialogHandler,
        showFilterPanel,
        // gridState, // TODO: Full save state
      }}
    >
      {/*
        Playwright marker: the SSRM tray is portaled into react-bootstrap's Modal,
        so we expose `data-extended-a-open` on a zero-size sentinel that's always
        present in the DOM and reflects the current open state + active report.
      */}
      <span
        data-extended-a-open={showReport ? "true" : "false"}
        data-extended-a-widget={reportConfigs.widgetUrl || undefined}
        style={{ display: "none" }}
      />
      <ReportDialog show={showReport} />
      <CalculatedFieldDg show={showCalculatedFieldDg} />
      <SavedViewsDialog show={showSavedViewsDg} />
      <SaveCurrentViewDialog show={showSaveCurrentViewDg} />
      {children}
    </ExtAContext.Provider>
  );
};

export default ExtAProvider;
